import { useCallback, useRef, useEffect } from "react"
import { useStore } from "react-redux"
import { omitBy, isUndefined } from "lodash"
import { dashboardEvents, DASHBOARD_EVENTS } from "helpers/events/dashboard-events"
import { useAnalytics } from "connected-components/analytics"
import { SHARED_EVENTS } from "connected-components/analytics/shared/events"
import type { ReduxState } from "model/state"
import { useAddModule } from "../hooks/use-add-module"
import { useUpdateModule } from "../hooks/use-update-module"
import { useRemoveModule } from "../hooks/use-remove-module"
import { useDashboardId } from "../context/dashboard-id"
import { EMPTY_MODULES } from "../store/panels-slice"
import { getPanelIdForModule } from "../helpers/module-renderers"
import { getDashboardLayoutPanelState } from "../helpers/dashboard-layout-panel-state"
import { selectEffectiveFilterScopeForBenchmark } from "../store/selectors/select-effective-filter-scope"
import { MODULE_TYPES } from "../modules/types"
import type { DashboardModule } from "../modules/types"
import { matchesDrilldownSeed, mergeDrilldownSeed } from "./drilldown-seed-utils"
import type { DrilldownIntent, DrilldownSeed } from "./types"

function buildDrilldownModuleConfig(intent: DrilldownIntent, existingSeed?: DrilldownSeed) {
  return {
    title: intent.title,
    drilldownSeed: mergeDrilldownSeed(existingSeed, intent),
    // Only include floatingClickPosition when defined — an explicit undefined would
    // overwrite the stored anchor on refocus paths that don't carry a click position.
    ...(intent.clickPosition !== undefined && { floatingClickPosition: intent.clickPosition }),
  }
}

function buildTransientDrilldownModuleConfig(intent: DrilldownIntent, existingSeed?: DrilldownSeed) {
  return {
    initialView: intent.seed.view,
    isSecondary: true,
    floating: true,
    transient: true as const,
    ...buildDrilldownModuleConfig(intent, existingSeed),
  }
}

function stampFilterScope(intent: DrilldownIntent, state: ReduxState, dashboardId: string): DrilldownIntent {
  const frozenSnapshot = intent.seed.frozenSnapshot
  if (!frozenSnapshot) return intent

  const filterScope = selectEffectiveFilterScopeForBenchmark(state, dashboardId, frozenSnapshot.base)

  return { ...intent, seed: { ...intent.seed, frozenSnapshot: { ...frozenSnapshot, filterScope } } }
}

function focusDrilldownPanel(panelId: string): void {
  dashboardEvents.emit(DASHBOARD_EVENTS.DRILLDOWN_FOCUS_PANEL, { panelId })
}

function refocusExistingDrilldownModule(
  existingModule: DashboardModule,
  intent: DrilldownIntent,
  updateModule: ReturnType<typeof useUpdateModule>,
  removeModule: ReturnType<typeof useRemoveModule>,
): boolean {
  const panelId = getPanelIdForModule(existingModule)
  const newConfig = buildTransientDrilldownModuleConfig(intent, existingModule.config.drilldownSeed)

  const layoutState = getDashboardLayoutPanelState()
  if (!layoutState) {
    updateModule(existingModule.id, { config: newConfig })
    focusDrilldownPanel(panelId)
    return true
  }

  if (layoutState.isPanelMounted(panelId)) {
    updateModule(existingModule.id, { config: newConfig })
    focusDrilldownPanel(panelId)
    return true
  }

  if (layoutState.ensurePanel(panelId)) {
    updateModule(existingModule.id, { config: newConfig })
    focusDrilldownPanel(panelId)
    return true
  }

  // Module record survived in panelsSlice but has no live Dockview panel — drop it and open fresh.
  removeModule(existingModule)
  return false
}

export function useDrilldownOpener(): (intent: DrilldownIntent) => void {
  const store = useStore<ReduxState>()
  const dashboardId = useDashboardId()
  const addModule = useAddModule()
  const updateModule = useUpdateModule()
  const removeModule = useRemoveModule()
  const analytics = useAnalytics()
  // Hold the latest analytics in a ref so the opener callback stays stable across analytics
  // context changes (model/mode switches change its reference); only .track is used, read inside
  // the user-triggered callback. Updated in an effect (not during render) to satisfy the refs rule.
  const analyticsRef = useRef(analytics)
  useEffect(() => {
    analyticsRef.current = analytics
  }, [analytics])

  return useCallback((sourceIntent: DrilldownIntent) => {
    const state = store.getState()
    const intent = stampFilterScope(sourceIntent, state, dashboardId)
    // Single chokepoint for every graph-line / factor-row drill-down into an asset view.
    // sourceSurface identifies the originating module (graph.* / factors.*). See APP-3222.
    analyticsRef.current.track(SHARED_EVENTS.DRILLDOWN_OPENED, omitBy({
      source_surface: intent.sourceSurface,
      view: intent.seed.view,
      factor_id: intent.seed.selectedFactor,
      category: intent.seed.selectedCategory,
      metric_display: intent.seed.selectedMetricDisplay,
      clicked_date: intent.seed.clickedDate,
      clicked_item_id: intent.seed.clickedItemId,
      model_id: intent.seed.frozenSnapshot?.modelId,
      model_name: intent.seed.frozenSnapshot?.modelName,
      target_module: intent.targetModuleType,
    }, isUndefined))

    // Read live modules from the store inside the callback rather than capturing them in
    // a ref — store.getState() reflects any just-dispatched module synchronously, and
    // avoids a captured-closure ref that could drift from the live state.
    const modules = state.dashboard.panels[dashboardId]?.modules ?? EMPTY_MODULES

    const existingModule = modules.find((module) => {
      if (!module.config?.transient) return false
      return matchesDrilldownSeed(module.config.drilldownSeed, intent.seed, intent.sourcePanelId)
    })

    if (existingModule && refocusExistingDrilldownModule(existingModule, intent, updateModule, removeModule)) {
      return
    }

    addModule({
      type: MODULE_TYPES.ASSET_VIEW,
      config: buildTransientDrilldownModuleConfig(intent),
    })
  }, [store, dashboardId, addModule, updateModule, removeModule])
}
